// Use relative path for single-URL setup (works in both dev and production)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface LoginCredentials {
  email: string;
  password: string;
  tenantSlug?: string;
}

export interface AuthResponse {
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    tenantId: string;
  };
}

export interface TenantData {
  tenant: {
    id: string;
    slug: string;
    name: string;
    isActive: boolean;
  };
  config: {
    agentsConfigUrl?: string;
    pageTitle?: string;
    companyName?: string;
    logoBase64?: string;
    logoPath?: string;
    useTextLogo?: boolean;
    logoLightBrightness?: string;
    logoLightContrast?: string;
    logoLightDropShadowOpacity?: string;
    logoDarkBrightness?: string;
    logoDarkContrast?: string;
    logoDarkDropShadowOpacity?: string;
    customFavicon?: string;
    welcomeMessage?: string;
    useCustomButton?: boolean;
    customButtonImage?: string;
    customButtonText?: string;
    streamingEnabled?: boolean;
    chatTitle?: string;
    openidConfig?: string | {
      enabled?: boolean;
      providerName?: string;
      logoUrl?: string;
      clientId?: string;
      clientSecret?: string;
      authority?: string;
      authorizeUrl?: string;
      tokenUrl?: string;
      profileUrl?: string;
      scope?: string;
      jsonPathEmail?: string;
      jsonPathName?: string;
      redirectUri?: string;
    };
    openidButtonConfig?: string | {
      template?: string;
      classes?: string;
      imageUrl?: string;
    };
    localLoginEnabled?: boolean;
    iconConfig?: string | Record<string, string>;
  } | null;
  theme: {
    // Light theme
    lightBackground?: string;
    lightForeground?: string;
    lightPrimary?: string;
    lightPrimaryForeground?: string;
    lightSecondary?: string;
    lightSecondaryForeground?: string;
    lightMuted?: string;
    lightMutedForeground?: string;
    lightAccent?: string;
    lightAccentForeground?: string;
    lightDestructive?: string;
    lightDestructiveForeground?: string;
    lightBorder?: string;
    lightInput?: string;
    lightRing?: string;
    lightRadius?: string;
    lightChart1?: string;
    lightChart2?: string;
    lightChart3?: string;
    lightChart4?: string;
    lightChart5?: string;
    // Dark theme
    darkBackground?: string;
    darkForeground?: string;
    darkPrimary?: string;
    darkPrimaryForeground?: string;
    darkSecondary?: string;
    darkSecondaryForeground?: string;
    darkMuted?: string;
    darkMutedForeground?: string;
    darkAccent?: string;
    darkAccentForeground?: string;
    darkDestructive?: string;
    darkDestructiveForeground?: string;
    darkBorder?: string;
    darkInput?: string;
    darkRing?: string;
    darkRadius?: string;
    darkChart1?: string;
    darkChart2?: string;
    darkChart3?: string;
    darkChart4?: string;
    darkChart5?: string;
  } | null;
}

// Utility function to convert agent name to URL slug
export function agentNameToSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters except word chars, spaces, and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  private getAuthHeader(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }

  private async fetchWithInterceptor(url: string | URL, options?: RequestInit): Promise<Response> {
    const response = await fetch(url, options);

    // Check for 401 Unauthorized response
    // Exclude /auth/login endpoint to prevent clearing auth during login attempts
    if (response.status === 401 && !url.toString().includes('/auth/login')) {
      // Clear authentication state
      this.logout();
      
      // Redirect to root path (which will show login page)
      window.location.href = '/';
      
      // Return a rejected promise to prevent further processing
      throw new Error('Unauthorized: Session expired');
    }

    return response;
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    
    // Store token
    localStorage.setItem('auth_token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    return data;
  }

  logout() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }

  getStoredUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  async getCurrentUser() {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/auth/me`, {
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to get current user');
    }

    const user = await response.json();
    
    // Store user info
    localStorage.setItem('user', JSON.stringify(user));
    
    return user;
  }

  async getTenantBySlug(slug: string): Promise<TenantData> {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/by-slug/${slug}`);

    if (!response.ok) {
      throw new Error('Tenant not found');
    }

    const data = await response.json();
    
    return data;
  }

  async getTenantConfig(tenantId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/config`, {
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch config');
    }

    return response.json();
  }

  async updateTenantConfig(tenantId: string, config: any) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/config`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      throw new Error('Failed to update config');
    }
    
    return response.json();
  }

  async getTenantTheme(tenantId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/theme`, {
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch theme');
    }

    return response.json();
  }

  async updateTenantTheme(tenantId: string, theme: any) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/theme`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(theme),
    });

    if (!response.ok) {
      throw new Error('Failed to update theme');
    }

    return response.json();
  }

  async getTenantAgents(tenantId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/agents`, {
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch agents');
    }

    return response.json();
  }

  async getPublicAgent(tenantSlug: string, agentNameSlug: string) {
    const response = await fetch(`${this.baseUrl}/tenants/by-slug/${tenantSlug}/public/agents/${agentNameSlug}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch public agent');
    }

    return response.json();
  }

  async createAgent(tenantId: string, agentData: any) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/agents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(agentData),
    });

    if (!response.ok) {
      throw new Error('Failed to create agent');
    }

    return response.json();
  }

  async updateAgent(tenantId: string, agentId: string, agentData: any) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/agents/${agentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(agentData),
    });

    if (!response.ok) {
      throw new Error('Failed to update agent');
    }

    return response.json();
  }

  async deleteAgent(tenantId: string, agentId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/agents/${agentId}`, {
      method: 'DELETE',
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete agent');
    }

    return response.json();
  }

  // Embedded Content methods
  async getTenantEmbeddedContents(tenantId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/embedded-contents`, {
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch embedded contents');
    }

    return response.json();
  }

  async createEmbeddedContent(tenantId: string, embeddedContentData: any) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/embedded-contents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(embeddedContentData),
    });

    if (!response.ok) {
      throw new Error('Failed to create embedded content');
    }

    return response.json();
  }

  async updateEmbeddedContent(tenantId: string, embeddedContentId: string, embeddedContentData: any) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/embedded-contents/${embeddedContentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(embeddedContentData),
    });

    if (!response.ok) {
      throw new Error('Failed to update embedded content');
    }

    return response.json();
  }

  async deleteEmbeddedContent(tenantId: string, embeddedContentId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/embedded-contents/${embeddedContentId}`, {
      method: 'DELETE',
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete embedded content');
    }

    return response.json();
  }

  // Home Screen methods
  async getTenantHomeScreenItems(tenantId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/home-screen-items`, {
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch home screen items');
    }

    return response.json();
  }

  async createHomeScreenItem(tenantId: string, homeScreenItemData: any) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/home-screen-items`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(homeScreenItemData),
    });

    if (!response.ok) {
      throw new Error('Failed to create home screen item');
    }

    return response.json();
  }

  async deleteHomeScreenItem(tenantId: string, itemId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/home-screen-items/${itemId}`, {
      method: 'DELETE',
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete home screen item');
    }

    return response.json();
  }

  async updateHomeScreenOrder(tenantId: string, orderUpdates: any[]) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/home-screen-items/order`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify({ updates: orderUpdates }),
    });

    if (!response.ok) {
      throw new Error('Failed to update home screen order');
    }

    return response.json();
  }

  async replaceHomeScreen(tenantId: string, homeScreenItems: any[]) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/home-screen-items/replace`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify({ items: homeScreenItems }),
    });

    if (!response.ok) {
      throw new Error('Failed to save home screen configuration');
    }

    return response.json();
  }

  async updateHomeScreenItems(tenantId: string, homeScreenItems: any[]) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/home-screen-items/bulk`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify({ items: homeScreenItems }),
    });

    if (!response.ok) {
      throw new Error('Failed to update home screen items');
    }

    return response.json();
  }

  async getTenantLanguages(tenantId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/languages`, {
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch languages');
    }

    return response.json();
  }

  async createLanguage(tenantId: string, languageData: any) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/languages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(languageData),
    });

    if (!response.ok) {
      throw new Error('Failed to create language');
    }

    return response.json();
  }

  async getLanguageTranslations(tenantId: string, languageId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/translations/${languageId}`, {
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch translations');
    }

    return response.json();
  }

  async getLanguageTranslationsByLanguageCode(tenantId: string, languageCode: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/translationsByLanguageCode/${languageCode}`, {
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch translations');
    }

    return response.json();
  }

  async upsertTranslation(tenantId: string, languageId: string, translationData: any) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/translations/${languageId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(translationData),
    });

    if (!response.ok) {
      throw new Error('Failed to save translation');
    }

    return response.json();
  }

  // User Management API methods
  async getTenantUsers(tenantId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    return response.json();
  }

  async createUser(tenantId: string, userData: {
    name: string;
    email: string;
    password: string;
    role?: string;
    isActive?: boolean;
  }) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create user');
    }

    return response.json();
  }

  async updateUser(tenantId: string, userId: string, userData: {
    name?: string;
    email?: string;
    role?: string;
    isActive?: boolean;
  }) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update user');
    }

    return response.json();
  }

  async deleteUser(tenantId: string, userId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete user');
    }

    return response.json();
  }

  async changePassword(userId: string, passwordData: {
    currentPassword: string;
    newPassword: string;
  }) {
    const user = this.getStoredUser();
    if (!user?.tenantId) {
      throw new Error('User not authenticated');
    }

    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${user.tenantId}/users/${userId}/change-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(passwordData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to change password');
    }

    return response.json();
  }

  async updateTenantOpenIDConfig(tenantId: string, config: {
    enabled: boolean;
    providerName?: string;
    logoUrl?: string;
    clientId: string;
    clientSecret: string;
    authorizeUrl?: string;
    tokenUrl?: string;
    profileUrl?: string;
    scope?: string;
    jsonPathEmail?: string;
    jsonPathName?: string;
    jsonPathOidcUserId?: string;
    authority?: string;
    redirectUri: string;
    buttonTemplate?: string;
    buttonClasses?: string;
    buttonImageUrl?: string;
  }) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/openid-config`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update OpenID configuration');
    }

    return response.json();
  }

  async getTenantOpenIDConfig(tenantId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/openid-config`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch OpenID configuration');
    }

    return response.json();
  }

  async validateOIDCUser(tenantId: string, email: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/validate-oidc-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || 'User validation failed');
    }

    return response.json();
  }

  // User Group Management API methods
  async getTenantUserGroups(tenantId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/user-groups`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user groups');
    }

    return response.json();
  }

  async createUserGroup(tenantId: string, groupData: {
    name: string;
    description?: string;
    userIds?: string[];
  }) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/user-groups`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(groupData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create user group');
    }

    return response.json();
  }

  async updateUserGroup(tenantId: string, groupId: string, groupData: {
    name?: string;
    description?: string;
    userIds?: string[];
  }) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/user-groups/${groupId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(groupData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update user group');
    }

    return response.json();
  }

  async deleteUserGroup(tenantId: string, groupId: string) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/user-groups/${groupId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete user group');
    }

    return response.json();
  }

  async updateHomeScreenItem(tenantId: string, itemId: string, itemData: {
    name?: string;
    showTitle?: boolean;
    groupAccess?: string[];
  }) {
    const response = await this.fetchWithInterceptor(`${this.baseUrl}/tenants/${tenantId}/home-screen-items/${itemId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(itemData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update home screen item');
    }

    return response.json();
  }
}

export const apiService = new ApiService();

